import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * MentionTextarea — a drop-in textarea replacement that surfaces a lightweight
 * `@name` autocomplete popover backed by /api/team/mentionable. Inserts the
 * chosen handle inline as `@firstname` (with a trailing space). Members are
 * fetched once on mount and cached in-component; kept intentionally small.
 */
export default function MentionTextarea({
  value,
  onChange,
  rows = 2,
  placeholder = "",
  className = "",
  testId,
}) {
  const [members, setMembers] = useState([]);
  const [pop, setPop] = useState(null); // { query, top, left, targetIndex }
  const [activeIdx, setActiveIdx] = useState(0);
  const taRef = useRef(null);
  const mirrorRef = useRef(null);

  useEffect(() => {
    api.get("/team/mentionable")
      .then(({ data }) => setMembers(data?.members || []))
      .catch(() => setMembers([]));
  }, []);

  // Scan for an active `@token` that the caret currently sits inside.
  const scanCaretMention = () => {
    const el = taRef.current;
    if (!el) return null;
    const pos = el.selectionStart ?? 0;
    const upto = value.slice(0, pos);
    // Match a trailing `@handle` (handle may be empty right after typing `@`).
    const m = upto.match(/(?:^|[\s(\[])@([A-Za-z0-9_.\-]{0,30})$/);
    if (!m) return null;
    return { query: m[1].toLowerCase(), start: pos - m[1].length - 1, end: pos };
  };

  const updatePop = () => {
    const scan = scanCaretMention();
    if (!scan) { setPop(null); return; }
    setActiveIdx(0);
    setPop(scan);
  };

  useEffect(() => { updatePop(); /* eslint-disable-next-line */ }, [value]);

  const filtered = pop
    ? members
        .filter((m) => {
          const q = pop.query;
          if (!q) return true;
          const local = (m.email || "").split("@")[0].toLowerCase();
          const name = (m.name || "").toLowerCase();
          return (
            m.handle?.startsWith(q) ||
            local.startsWith(q) ||
            name.split(" ").some((p) => p.startsWith(q)) ||
            name.startsWith(q)
          );
        })
        .slice(0, 6)
    : [];

  const insertMention = (m) => {
    if (!pop) return;
    const handle = (m.handle || (m.email || "").split("@")[0] || "user");
    const before = value.slice(0, pop.start);
    const after = value.slice(pop.end);
    const next = `${before}@${handle} ${after}`;
    onChange({ target: { value: next } });
    setPop(null);
    // Restore caret just after the inserted mention on the next tick.
    requestAnimationFrame(() => {
      const el = taRef.current;
      if (!el) return;
      const pos = before.length + handle.length + 2;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const onKeyDown = (e) => {
    if (!pop || filtered.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % filtered.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => (i - 1 + filtered.length) % filtered.length); }
    else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insertMention(filtered[activeIdx]);
    } else if (e.key === "Escape") {
      setPop(null);
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={taRef}
        data-testid={testId}
        rows={rows}
        className={className}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onKeyUp={updatePop}
        onClick={updatePop}
        placeholder={placeholder}
      />
      {pop && filtered.length > 0 && (
        <div
          data-testid="mention-popover"
          className="absolute z-40 left-2 top-full mt-1 min-w-[220px] bg-white border-2 border-[var(--peak-ink)] shadow-[3px_3px_0_0_var(--peak-shadow)]"
        >
          <div className="section-label px-3 pt-2 pb-1">Mention teammate</div>
          {filtered.map((m, idx) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
              data-testid={`mention-option-${m.handle}`}
              className={`w-full text-left px-3 py-1.5 border-t border-[var(--peak-border)] flex items-center gap-2 ${
                idx === activeIdx ? "bg-[var(--peak-surface-alt)]" : "hover:bg-[var(--peak-surface-alt)]"
              }`}
            >
              <div className="w-6 h-6 bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)] flex items-center justify-center text-[10px] font-black font-heading">
                {(m.name || m.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold uppercase tracking-widest truncate">{m.name || m.email}</div>
                <div className="text-[10px] font-mono text-[var(--peak-muted)] truncate">@{m.handle}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      {/* mirror is unused for now but kept in DOM to leave room for caret-anchored positioning later */}
      <div ref={mirrorRef} style={{ display: "none" }} />
    </div>
  );
}
